import {
  type MediaAsset,
  type NormalizedMediaAsset,
  normalizeMediaArray,
  normalizeMediaAsset,
} from "./types";

export * from "./blurhash";
export * from "./paths";
export * from "./types";

const VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "3gp", "3gpp", "m4v", "m3u8"];

export const isVideoUrl = (url: string): boolean => {
  const normalized = url.toLowerCase();

  try {
    const parsed = new URL(url);
    const pathname = parsed.pathname;
    const extensionMatch = pathname.match(/\.([a-z0-9]+)$/);
    if (!extensionMatch) {
      return normalized.includes("/video/");
    }
    return VIDEO_EXTENSIONS.includes(extensionMatch[1]);
  } catch {
    const extensionMatch = normalized.match(/\.([a-z0-9]+)(?:\?|#|$)/);
    if (!extensionMatch) {
      return normalized.includes("/video/");
    }
    return VIDEO_EXTENSIONS.includes(extensionMatch[1]);
  }
};

export const toRenderableMedia = (
  value: MediaAsset,
): NormalizedMediaAsset | null => normalizeMediaAsset(value);

export const toRenderableMediaArray = (
  value: unknown,
): NormalizedMediaAsset[] => normalizeMediaArray(value);

export const getVideoPosterFromItem = (
  item: MediaAsset,
): string | undefined => {
  const normalized = normalizeMediaAsset(item);
  if (normalized?.kind !== "video") {
    return undefined;
  }
  return normalized.poster?.url;
};

export const isVideoItem = (item: MediaAsset): boolean => {
  const normalized = normalizeMediaAsset(item);
  return normalized?.kind === "video";
};

export const mediaUrlFromAsset = (
  value: MediaAsset | null | undefined,
): string | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizeMediaAsset(value);
  return normalized?.url ?? null;
};

export const mediaPosterUrlFromAsset = (
  value: MediaAsset | null | undefined,
): string | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizeMediaAsset(value);
  return normalized?.poster?.url ?? null;
};

export const firstMediaUrl = (
  value: unknown,
  fallback?: string | null,
): string | null => {
  const normalized = normalizeMediaArray(value);
  const first = normalized.at(0);
  return first?.url ?? fallback ?? null;
};

/**
 * Resolves the avatar used for a person-facing surface.
 *
 * An avatar is ONLY ever a photo the person uploaded to Hiive. An
 * auth-provider image (Google's `picture`, for one) is not: nobody chose to
 * publish it here, it is hosted on the provider's CDN — so every viewer of a
 * list leaks their IP and the page they are on to Google — and it can change or
 * disappear without us knowing. There is no `authImage` fallback for that
 * reason, not even while a profile is still incomplete: no profile media means
 * the caller renders its initials placeholder.
 *
 * `user.image` still HOLDS the provider's URL — Better Auth writes it at signup
 * and it is kept on purpose. It is simply never read on a rendering path; if
 * something ever needs it, that is a deliberate new decision, not a fallback.
 *
 * `profileIsComplete` is accepted so call sites keep reading the flag they
 * already select, and so a completed profile with missing/malformed media stays
 * visible as the data-integrity issue it is.
 *
 * A video profile resolves to its uploaded poster — avatars are image-only
 * surfaces. The video itself is rendered by `ProfileMedia` on the profile,
 * Discover, buddy and Search result surfaces that opt into autoplay.
 */
export const resolveProfileAvatarUrl = ({
  profileImages,
}: {
  profileImages: unknown;
  profileIsComplete?: boolean | null | undefined;
}): string | null => firstMediaPreviewUrl(profileImages);

/**
 * A URL that is safe to render in an image-only surface. Images return their
 * own URL; videos return the uploaded poster (or the fallback), never the
 * video binary. This prevents `<img src="…mp4">` breakage in share cards,
 * map thumbnails, admin rows, and other compact previews.
 */
export const mediaPreviewUrlFromAsset = (
  value: MediaAsset | null | undefined,
  fallback?: string | null,
): string | null => {
  if (!value) return fallback ?? null;
  const normalized = normalizeMediaAsset(value);
  if (!normalized) return fallback ?? null;
  return normalized.kind === "video"
    ? (normalized.poster?.url ?? fallback ?? null)
    : normalized.url;
};

export const firstMediaPreviewUrl = (
  value: unknown,
  fallback?: string | null,
): string | null => {
  const first = normalizeMediaArray(value).at(0);
  return first
    ? first.kind === "video"
      ? (first.poster?.url ?? fallback ?? null)
      : first.url
    : (fallback ?? null);
};

export const mediaUrlsFromArray = (value: unknown): string[] =>
  normalizeMediaArray(value).map((item) => item.url);

/**
 * A preview image plus the pixel dimensions of the exact derivative the URL
 * points at.
 *
 * Social crawlers use `og:image:width`/`og:image:height` to decide, before the
 * image itself has downloaded, whether a link renders as a large card or a
 * thumbnail — WhatsApp and Facebook both want at least 300x200 and both fall
 * back to the small layout when they cannot tell. Declaring them is also the
 * only way to stop a crawler that guesses wrong from cropping a 16:9 banner to
 * a square.
 *
 * The numbers have to describe the *derivative* in the URL, not the original
 * upload: `asset.width`/`height` are the dimensions of the file the user
 * picked, while the key usually resolves to a `-1080`/`-1600` rung of the
 * processor's width ladder. `variants` carries the measured pair per rung, and
 * the aspect-scaled fallback exists for assets uploaded before the ladder did.
 */
export type PreviewImage = {
  url: string;
  width?: number;
  height?: number;
};

const VARIANT_WIDTH_IN_KEY = /-(\d+)\/[^/]+$/;

const dimensionsForUrl = (
  asset: NormalizedMediaAsset,
  url: string,
): PreviewImage => {
  const variant = asset.variants?.find((item) => url.endsWith(item.key));
  if (variant?.width && variant.height) {
    return { url, width: variant.width, height: variant.height };
  }

  const renderedWidth = Number(url.match(VARIANT_WIDTH_IN_KEY)?.[1]);
  if (
    Number.isFinite(renderedWidth) &&
    renderedWidth > 0 &&
    asset.width &&
    asset.height
  ) {
    // The ladder never upscales, so a rung wider than the original means the
    // processor served the original instead.
    const width = Math.min(renderedWidth, asset.width);
    return {
      url,
      width,
      height: Math.round((width * asset.height) / asset.width),
    };
  }

  return asset.width && asset.height
    ? { url, width: asset.width, height: asset.height }
    : { url };
};

/** {@link mediaPreviewUrlFromAsset}, carrying the derivative's dimensions. */
export const mediaPreviewImageFromAsset = (
  value: MediaAsset | null | undefined,
  fallback?: PreviewImage | string | null,
): PreviewImage | null => {
  const asFallback =
    typeof fallback === "string" ? { url: fallback } : (fallback ?? null);
  if (!value) return asFallback;
  const normalized = normalizeMediaAsset(value);
  if (!normalized) return asFallback;
  if (normalized.kind === "video") {
    // A video's poster is a separate derivative with no recorded dimensions of
    // its own, so only its URL is safe to publish.
    return normalized.poster ? { url: normalized.poster.url } : asFallback;
  }
  return dimensionsForUrl(normalized, normalized.url);
};

/** {@link firstMediaPreviewUrl}, carrying the derivative's dimensions. */
export const firstMediaPreviewImage = (
  value: unknown,
  fallback?: PreviewImage | string | null,
): PreviewImage | null => {
  const first = normalizeMediaArray(value).at(0);
  return first
    ? mediaPreviewImageFromAsset(first, fallback)
    : typeof fallback === "string"
      ? { url: fallback }
      : (fallback ?? null);
};
